import { useMemo } from 'react';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import { useGetPolylineQuery } from '../../redux/mapsApi';
import { LngLat } from '../../types/Geo';
import { decodeSegmentLngLat } from '../../utils/legacyGeoJSONConversions';
import Polyline from '../Polyline';

type TrailPolylineProps = {
  trail: Pick<TrailSearchResult, 'ID' | 'polyline'>;
  /** Defaults to the trail ID; pass another to draw the same trail twice without a source id clash */
  uniqueId?: string;
};

const FetchedTrailPolyline = ({ trailId, uniqueId }: { trailId: number; uniqueId: string }) => {
  const { data } = useGetPolylineQuery({ trailId });

  if (!data) {
    return null;
  }

  return <Polyline coordinates={data} uniqueId={uniqueId} />;
};

const CachedTrailPolyline = ({ polyline, uniqueId }: { polyline: string; uniqueId: string }) => {
  // A new array each render would rebuild the line and re-upload it to the map source
  const coordinates = useMemo(() => decodeSegmentLngLat({ polyline: { pointsData: polyline } }) as LngLat[], [polyline]);

  return <Polyline coordinates={coordinates} uniqueId={uniqueId} />;
};

// Draws from the search result's own polyline when it has one, and fetches it otherwise. Keyed by `uniqueId` because
// react-map-gl fixes a Source's id on mount and throws if it changes, so a different trail needs a fresh Source.
const TrailPolyline = ({ trail: { ID, polyline }, uniqueId = String(ID) }: TrailPolylineProps) =>
  polyline ? (
    <CachedTrailPolyline key={uniqueId} polyline={polyline} uniqueId={uniqueId} />
  ) : (
    <FetchedTrailPolyline key={uniqueId} trailId={ID} uniqueId={uniqueId} />
  );

export default TrailPolyline;
